import React, { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { Map, Satellite, Layers } from 'lucide-react';

const TILE_LAYERS = {
  dark: {
    // iter 304.21 — CartoCDN's dark basemap started returning "API KEY
    // REQUIRED" watermarks in Feb 2026. Swapped to OSM tiles + a CSS
    // filter (see .leaflet-tile-dark) so the dark look is preserved
    // without any tile provider auth.
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    className: 'leaflet-tile-dark',
  },
  satellite: {
    // iter 304.20 — USGS ImageryOnly endpoint started returning "API required"
    // errors for some tenants after their July throttle policy. Esri's
    // public World_Imagery ArcGIS Online endpoint is still key-less for
    // reasonable free-tier use and covers the whole world with fresh
    // imagery. This is the classic keyless satellite tile source.
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
  },
  traffic: {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  }
};

/**
 * MapLayerSwitch - Swaps tile layers inside MapContainer. Must be a child of <MapContainer>.
 */
export const MapLayerSwitch = ({ mapLayer }) => {
  const map = useMap();

  useEffect(() => {
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer);
      }
    });
    const config = TILE_LAYERS[mapLayer] || TILE_LAYERS.dark;
    const opts = { attribution: config.attribution };
    if (config.subdomains) opts.subdomains = config.subdomains;
    if (config.className) opts.className = config.className;
    L.tileLayer(config.url, opts).addTo(map);
  }, [map, mapLayer]);

  return null;
};

/**
 * MapLayerButtons - UI toggle buttons for Map/Satellite/Traffic. 
 * Parent manages state via mapLayer + setMapLayer props.
 */
export const MapLayerButtons = ({ mapLayer, setMapLayer }) => {
  const { t } = useTranslation();

  return (
    <div className="flex rounded-lg overflow-hidden border border-slate-700 bg-slate-900/90 backdrop-blur-sm">
      <button
        onClick={() => setMapLayer('dark')}
        data-testid="map-layer-dark"
        className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors ${
          mapLayer === 'dark' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
        }`}
      >
        <Map className="h-3.5 w-3.5" />
        {t('tracking.map', 'Map')}
      </button>
      <button
        onClick={() => setMapLayer('satellite')}
        data-testid="map-layer-satellite"
        className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors border-l border-slate-700 ${
          mapLayer === 'satellite' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
        }`}
      >
        <Satellite className="h-3.5 w-3.5" />
        {t('tracking.satellite', 'Satellite')}
      </button>
      <button
        onClick={() => setMapLayer('traffic')}
        data-testid="map-layer-traffic"
        className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors border-l border-slate-700 ${
          mapLayer === 'traffic' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
        }`}
      >
        <Layers className="h-3.5 w-3.5" />
        {t('tracking.traffic', 'Traffic')}
      </button>
    </div>
  );
};

export default MapLayerSwitch;
